import type { NetworkError, NetworkErrorType } from '@/types/api'

/**
 * 错误处理工具类
 * 提供统一的错误处理和用户友好的错误信息显示
 */
export class ErrorHandler {
  /**
   * 将网络错误转换为用户友好的消息
   */
  static getErrorMessage(error: NetworkError): string {
    switch (error.type) {
      case 'TIMEOUT':
        return '请求超时，请检查网络连接后重试'
      case 'CONNECTION_ERROR':
        return '网络连接失败，请检查网络设置'
      case 'AUTH_ERROR':
        return '认证失败，请重新登录'
      case 'VALIDATION_ERROR':
        return '输入数据有误，请检查后重试'
      case 'SERVER_ERROR':
        return '服务器暂时不可用，请稍后再试'
      case 'UNKNOWN_ERROR':
      default:
        return error.message || '发生未知错误，请稍后再试'
    }
  }

  /**
   * 获取错误类型的中文描述
   */
  static getErrorTypeDescription(type: NetworkErrorType): string {
    switch (type) {
      case 'TIMEOUT':
        return 'TIME OUT'
      case 'CONNECTION_ERROR':
        return 'CONNECTION ERROR'
      case 'AUTH_ERROR':
        return 'AUTH ERROR'
      case 'VALIDATION_ERROR':
        return 'VALIDATION ERROR'
      case 'SERVER_ERROR':
        return 'SERVER ERROR'
      case 'UNKNOWN_ERROR':
      default:
        return 'UNKNOWN ERROR'
    }
  }

  /**
   * 根据错误状态码获取建议的解决方案
   */
  static getErrorSuggestion(error: NetworkError): string {
    if (error.status) {
      switch (error.status) {
        case 400:
          return '请检查请求参数是否正确'
        case 401:
          return '请重新登录后再试'
        case 403:
          return '您没有权限执行此操作'
        case 404:
          return '请求的资源不存在，请检查URL是否正确'
        case 422:
          return '请检查输入的数据格式是否正确'
        case 429:
          return '请求过于频繁，请稍后再试'
        case 500:
          return '服务器内部错误，请联系技术支持'
        case 502:
          return '网关错误，请稍后再试'
        case 503:
          return '服务暂时不可用，请稍后再试'
        case 504:
          return '网关超时，请稍后再试'
        default:
          return '请稍后再试，如问题持续存在请联系技术支持'
      }
    }

    switch (error.type) {
      case 'TIMEOUT':
        return '请检查网络连接，或尝试刷新页面'
      case 'CONNECTION_ERROR':
        return '请检查网络设置，确保能够访问服务器'
      case 'AUTH_ERROR':
        return '请重新登录，或联系管理员检查账户状态'
      case 'VALIDATION_ERROR':
        return '请检查输入的数据格式和内容'
      case 'SERVER_ERROR':
        return '请稍后再试，如问题持续存在请联系技术支持'
      default:
        return '请刷新页面重试，如问题持续存在请联系技术支持'
    }
  }

  /**
   * 判断错误是否为网络相关错误
   */
  static isNetworkError(error: NetworkError): boolean {
    return error.type === 'TIMEOUT' || error.type === 'CONNECTION_ERROR'
  }

  /**
   * 判断错误是否为认证相关错误
   */
  static isAuthError(error: NetworkError): boolean {
    return error.type === 'AUTH_ERROR' || error.status === 401 || error.status === 403
  }

  /**
   * 判断错误是否为服务器错误
   */
  static isServerError(error: NetworkError): boolean {
    return error.type === 'SERVER_ERROR' || (error.status !== undefined && error.status >= 500)
  }

  /**
   * 判断错误是否为客户端错误
   */
  static isClientError(error: NetworkError): boolean {
    return error.status !== undefined && error.status >= 400 && error.status < 500
  }

  /**
   * 创建标准化的错误对象
   */
  static createError(
    type: NetworkErrorType,
    message: string,
    status?: number,
    details?: any
  ): NetworkError {
    return {
      type,
      message,
      status,
      details,
      code: details?.error_code || details?.code
    }
  }

  /**
   * 从 axios 错误创建网络错误对象
   */
  static fromAxiosError(axiosError: any): NetworkError {
    if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
      return this.createError('TIMEOUT', '请求超时')
    }

    if (!axiosError.response) {
      return this.createError('CONNECTION_ERROR', '网络连接失败')
    }

    const { status, data } = axiosError.response

    switch (status) {
      case 400:
        return this.createError('VALIDATION_ERROR', '请求参数错误', status, data)
      case 401:
      case 403:
        return this.createError('AUTH_ERROR', '认证失败', status, data)
      case 404:
        return this.createError('SERVER_ERROR', '请求的资源不存在', status, data)
      case 422:
        return this.createError('VALIDATION_ERROR', '数据验证失败', status, data)
      case 429:
        return this.createError('SERVER_ERROR', '请求过于频繁', status, data)
      case 500:
      case 502:
      case 503:
      case 504:
        return this.createError('SERVER_ERROR', '服务器错误', status, data)
      default:
        return this.createError('UNKNOWN_ERROR', `请求失败 (${status})`, status, data)
    }
  }

  /**
   * 记录错误到控制台（开发环境）
   */
  static logError(error: NetworkError, context?: string): void {
    if (import.meta.env.DEV) {
      const prefix = context ? `[${context}]` : '[ErrorHandler]'
      console.error(`${prefix} Error:`, {
        type: error.type,
        message: error.message,
        status: error.status,
        code: error.code,
        details: error.details
      })
    }
  }
}

export default ErrorHandler